/**
 * VisuelCard — analyse visuelle multimodale (Phase 3C).
 *
 * Layout :
 *   - Header "Analyse IA · Analyse visuelle".
 *   - Hero badges : style architectural + état apparent + mitoyenneté + orientation.
 *   - Grid de features booléennes (piscine, jardin, panneaux, terrasse, annexe,
 *     parking) avec icône ✓/✗/?.
 *   - Paragraphe narratif (Claude).
 *   - Miniatures sources : orthophoto IGN (toujours dispo) + 2 Street View
 *     (si Google Static a renvoyé du contenu).
 *   - Meta + footer.
 *
 * Quand `unavailableReason` est posé, on affiche une carte factuelle au lieu
 * de tout l'attirail. Pas de bannière rouge.
 *
 * Note Street View : le serveur n'écrit PAS la clé Google dans Firestore. La
 * miniature est reconstruite côté client avec `VITE_GOOGLE_MAPS_KEY`. Si la
 * clé n'est pas configurée en preview/dev, on affiche un fallback gris.
 */

import {
  useAiVisuel,
  buildStreetViewClientUrl,
  type AiVisuel,
  type AiVisuelFeatures,
} from "../../lib/ai/visuel";
import { Card } from "../ui";
import { Ic } from "../icons";
import type { ReactNode } from "react";

type Props = { ficheId: string };

export function VisuelCard({ ficheId }: Props) {
  const state = useAiVisuel(ficheId);

  return (
    <Card className="p-6 md:p-7">
      <header className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-ink-50 hairline grid place-items-center text-ink-700">
          <Ic.Eye size={18} />
        </div>
        <div>
          <div className="text-[11.5px] uppercase tracking-[0.12em] text-lime-700 font-medium">
            Analyse IA
          </div>
          <h2 className="text-[18px] md:text-[19px] font-semibold tracking-tight text-ink-900">
            Analyse visuelle
          </h2>
        </div>
      </header>

      <div className="mt-5">
        {state.status === "idle" || state.status === "loading" ? (
          <Skeleton />
        ) : state.status === "error" ? (
          <ErrorBox />
        ) : state.data.unavailableReason ? (
          <UnavailableBox data={state.data} />
        ) : (
          <ReadyView data={state.data} />
        )}
      </div>
    </Card>
  );
}

// ─── Ready view ──────────────────────────────────────────────────────────

function ReadyView({ data }: { data: AiVisuel }) {
  const hasAnyHeroBadge =
    data.architecturalStyle ||
    data.apparentState ||
    data.mitoyennete ||
    data.orientation;

  return (
    <div className="space-y-5">
      {hasAnyHeroBadge && <HeroBadges data={data} />}
      <FeaturesGrid features={data.features} />
      {data.paragraph && (
        <p className="text-[13.5px] leading-[1.65] text-ink-700">
          {data.paragraph}
        </p>
      )}
      <Sources data={data} />
      <Meta generatedAt={data.generatedAt} model={data.model} />
    </div>
  );
}

// ─── Hero (carte d'identité du bien) ────────────────────────────────────
//
// Layout en deux niveaux :
//   1. Style architectural (full-width, mis en avant) — quand renseigné.
//   2. Trois cellules côte à côte (état apparent · orientation · mitoyenneté)
//      séparées par des hairlines verticales (ou horizontales en mobile).
//
// Pour les 3 cellules, on affiche TOUJOURS le label, même quand le champ est
// `null` — avec un placeholder italique "Non observable". Ça évite le doute
// "est-ce que l'analyse a essayé ?" et garde la grille équilibrée.

function HeroBadges({ data }: { data: AiVisuel }) {
  return (
    <div className="rounded-2xl bg-gradient-to-br from-lime-50 via-paper to-paper hairline overflow-hidden">
      {data.architecturalStyle && (
        <div className="px-5 pt-5 pb-4 flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-paper hairline grid place-items-center text-ink-700 shrink-0">
            <Ic.Home size={16} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10.5px] uppercase tracking-[0.14em] text-ink-500 font-medium">
              Style architectural
            </div>
            <div className="mt-0.5 text-[15.5px] font-medium text-ink-900 leading-snug">
              {data.architecturalStyle}
            </div>
          </div>
        </div>
      )}

      <div
        className={`grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-ink-100/80 ${
          data.architecturalStyle ? "border-t border-ink-100/80" : ""
        }`}
      >
        <HeroCell
          label="État apparent"
          icon={<Ic.Sparkle size={11} />}
          value={
            data.apparentState ? (
              <StatePill state={data.apparentState} />
            ) : (
              <Placeholder label="Non observable" />
            )
          }
        />
        <HeroCell
          label="Orientation"
          icon={<Ic.Compass size={11} />}
          value={
            data.orientation ? (
              <span className="text-[14px] font-medium text-ink-900">
                {data.orientation}
              </span>
            ) : (
              <Placeholder label="Non observable" />
            )
          }
        />
        <HeroCell
          label="Mitoyenneté"
          icon={<Ic.Building size={11} />}
          value={
            data.mitoyennete ? (
              <span className="text-[14px] font-medium text-ink-900 first-letter:uppercase">
                {data.mitoyennete}
              </span>
            ) : (
              <Placeholder label="Non observable" />
            )
          }
        />
      </div>
    </div>
  );
}

function HeroCell({
  label,
  icon,
  value,
}: {
  label: string;
  icon: ReactNode;
  value: ReactNode;
}) {
  return (
    <div className="px-5 py-3.5">
      <div className="text-[10.5px] uppercase tracking-[0.14em] text-ink-500 font-medium flex items-center gap-1.5">
        <span className="text-ink-400 inline-flex">{icon}</span>
        {label}
      </div>
      <div className="mt-1 leading-snug">{value}</div>
    </div>
  );
}

function Placeholder({ label }: { label: string }) {
  return (
    <span className="text-[12.5px] italic text-ink-400">{label}</span>
  );
}

function StatePill({ state }: { state: NonNullable<AiVisuel["apparentState"]> }) {
  // Map state → colour. "neuf" et "correct" en lime ; "à rafraîchir" en
  // amber ; "à rénover" en rouge atténué. Pas d'alarme rouge vif —
  // l'analyse visuelle reste indicative.
  const tone: Record<NonNullable<AiVisuel["apparentState"]>, string> = {
    neuf: "bg-lime-50 text-lime-800 ring-lime-200",
    correct: "bg-lime-50 text-lime-800 ring-lime-200",
    "à rafraîchir": "bg-amber-50 text-amber-800 ring-amber-200",
    "à rénover": "bg-rose-50 text-rose-800 ring-rose-200",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[12px] font-medium ring-1 ring-inset ${tone[state]}`}
    >
      {state}
    </span>
  );
}

// ─── Features grid ──────────────────────────────────────────────────────

function FeaturesGrid({ features }: { features: AiVisuelFeatures }) {
  const items: Array<{
    key: keyof AiVisuelFeatures;
    label: string;
    icon: ReactNode;
  }> = [
    { key: "garden", label: "Jardin", icon: <Ic.Tree size={15} /> },
    { key: "pool", label: "Piscine", icon: <Ic.Droplet size={15} /> },
    { key: "solarPanels", label: "Panneaux solaires", icon: <Ic.Bolt size={15} /> },
    { key: "terrace", label: "Terrasse", icon: <Ic.Layers size={15} /> },
    { key: "annexBuilding", label: "Dépendance", icon: <Ic.Building size={15} /> },
    { key: "parking", label: "Stationnement", icon: <Ic.Pin size={15} /> },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
      {items.map((it) => (
        <FeatureChip
          key={it.key}
          label={it.label}
          icon={it.icon}
          value={features[it.key]}
        />
      ))}
    </div>
  );
}

function FeatureChip({
  label,
  icon,
  value,
}: {
  label: string;
  icon: ReactNode;
  value: boolean | null;
}) {
  // Trois états : true (présence confirmée — lime), false (absence confirmée —
  // gris), null (non observable — gris très clair, italique).
  const styles =
    value === true
      ? "bg-lime-50 text-lime-900 ring-lime-200"
      : value === false
        ? "bg-ink-50/70 text-ink-600 ring-ink-200"
        : "bg-paper text-ink-400 ring-ink-200";

  const indicator =
    value === true ? (
      <Ic.Check size={13} />
    ) : value === false ? (
      <Ic.Cross size={13} />
    ) : (
      <span className="text-[11px] font-medium">?</span>
    );

  return (
    <div
      className={`flex items-center gap-2 rounded-lg px-3 py-2 ring-1 ring-inset ${styles}`}
    >
      <span className="shrink-0">{icon}</span>
      <span className={`text-[13px] flex-1 min-w-0 ${value === null ? "italic" : ""}`}>
        {label}
      </span>
      <span className="shrink-0 w-5 h-5 grid place-items-center">
        {indicator}
      </span>
    </div>
  );
}

// ─── Sources (miniatures) ───────────────────────────────────────────────

function Sources({ data }: { data: AiVisuel }) {
  const sv0 =
    data.sources.streetView0 &&
    data.sources.streetView0.lat !== null &&
    data.sources.streetView0.lng !== null
      ? buildStreetViewClientUrl({
          lat: data.sources.streetView0.lat,
          lng: data.sources.streetView0.lng,
          heading: data.sources.streetView0.heading ?? 0,
          width: 320,
          height: 200,
        })
      : null;
  const sv180 =
    data.sources.streetView180 &&
    data.sources.streetView180.lat !== null &&
    data.sources.streetView180.lng !== null
      ? buildStreetViewClientUrl({
          lat: data.sources.streetView180.lat,
          lng: data.sources.streetView180.lng,
          heading: data.sources.streetView180.heading ?? 180,
          width: 320,
          height: 200,
        })
      : null;
  const ortho = data.sources.orthophoto.url;

  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.12em] text-ink-500 font-medium">
        Sources analysées
      </div>
      <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
        <SourceThumb
          label="Orthophoto IGN"
          url={ortho}
          attribution="© IGN — Géoplateforme"
        />
        <SourceThumb
          label="Vue rue · nord"
          url={sv0}
          attribution="© Google"
          unavailableHint={
            data.sources.streetViewAvailable
              ? "Configurez VITE_GOOGLE_MAPS_KEY pour la miniature."
              : "Pas de couverture Street View à cette adresse."
          }
        />
        <SourceThumb
          label="Vue rue · sud"
          url={sv180}
          attribution="© Google"
          unavailableHint={
            data.sources.streetViewAvailable
              ? "Configurez VITE_GOOGLE_MAPS_KEY pour la miniature."
              : "Pas de couverture Street View à cette adresse."
          }
        />
      </div>
    </div>
  );
}

function SourceThumb({
  label,
  url,
  attribution,
  unavailableHint,
}: {
  label: string;
  url: string | null;
  attribution: string;
  unavailableHint?: string;
}) {
  if (!url) {
    return (
      <div className="relative rounded-lg overflow-hidden bg-ink-50 hairline aspect-[3/2] flex flex-col items-center justify-center px-3 text-center">
        <div className="text-[11.5px] font-medium text-ink-700">{label}</div>
        {unavailableHint && (
          <div className="mt-1 text-[10.5px] text-ink-500 leading-tight">
            {unavailableHint}
          </div>
        )}
      </div>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative rounded-lg overflow-hidden bg-ink-900 hairline block aspect-[3/2]"
      title={`${label} — ouvrir`}
    >
      <img
        src={url}
        alt={label}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.opacity = "0";
        }}
      />
      <div className="absolute top-1.5 left-1.5 text-[10px] font-mono uppercase tracking-widest text-white/90 bg-black/45 px-1.5 py-0.5 rounded">
        {label}
      </div>
      <div className="absolute bottom-1.5 right-1.5 text-[9px] font-mono uppercase tracking-widest text-white/85 bg-black/45 px-1.5 py-0.5 rounded">
        {attribution}
      </div>
    </a>
  );
}

// ─── Meta + unavailable + error + skeleton ──────────────────────────────

function Meta({
  generatedAt,
  model,
}: {
  generatedAt: string;
  model: string;
}) {
  const date = formatDate(generatedAt);
  return (
    <div className="mt-1 pt-4 border-t border-ink-100 flex items-center gap-2 text-[11px] text-ink-500 leading-snug">
      <Ic.Sparkle size={11} />
      <span>
        Lecture visuelle indicative (orthophoto IGN
        {/* Mention Google seulement si on a effectivement utilisé Street View */}
        {/* — utile pour la transparence + l'attribution. */}
        {" "}+ Street View Google){" "}
        {date ? `générée le ${date}` : "générée"}
        {model ? ` · ${model}` : ""}. À confronter à une visite réelle.
      </span>
    </div>
  );
}

function UnavailableBox({ data }: { data: AiVisuel }) {
  return (
    <div className="flex items-start gap-3 rounded-lg bg-ink-50/70 hairline px-4 py-3">
      <div className="w-7 h-7 rounded-full bg-paper hairline grid place-items-center text-ink-600 shrink-0">
        <Ic.Sparkle size={13} />
      </div>
      <div>
        <div className="text-[13.5px] font-medium text-ink-900">
          Analyse visuelle non disponible.
        </div>
        <div className="text-[12.5px] text-ink-600 leading-snug mt-0.5">
          {data.unavailableReason}
        </div>
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="space-y-4 animate-pulse" aria-hidden>
      <div className="rounded-2xl bg-ink-100/70 h-[88px]" />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-[40px] rounded-lg bg-ink-100/60" />
        ))}
      </div>
      <div className="space-y-1.5">
        <div className="h-3 rounded bg-ink-100 w-11/12" />
        <div className="h-3 rounded bg-ink-100 w-3/4" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="aspect-[3/2] rounded-lg bg-ink-100/60" />
        ))}
      </div>
    </div>
  );
}

function ErrorBox() {
  return (
    <div className="rounded-lg bg-ink-50 px-4 py-3 text-[13px] text-ink-700 leading-relaxed">
      L'analyse visuelle IA est indisponible pour le moment. Réessayez dans
      quelques minutes.
    </div>
  );
}

// ─── Date formatting ─────────────────────────────────────────────────────
function formatDate(iso: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}
